import * as React from "react";
import { useCallback, useRef } from "react";
import { 
  ClipboardCheck, ArrowLeft, Search, AlertTriangle, CheckCircle2, 
  XCircle, Loader2, Mail, Clock, Bell, Plus, X, RefreshCw, 
  HelpCircle, Info, FileWarning, Link, Eye, ExternalLink
} from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Switch } from "@/components/ui/switch";
import axios from "axios";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const SummaryCard = ({ title, value, icon: Icon, color, subtext, tooltip }) => (
  <TooltipProvider>
    <Tooltip>
      <TooltipTrigger asChild>
        <Card className={`border-l-4 ${color} cursor-help transition-all hover:shadow-md`}>
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">{title}</p>
                <p className="text-2xl font-bold">{value}</p>
                {subtext && <p className="text-xs text-slate-400 mt-1">{subtext}</p>}
              </div>
              <Icon className="h-8 w-8 text-slate-300" />
            </div>
          </CardContent>
        </Card>
      </TooltipTrigger>
      {tooltip && (
        <TooltipContent className="max-w-xs">
          <p className="text-xs">{tooltip}</p>
        </TooltipContent>
      )}
    </Tooltip>
  </TooltipProvider>
);

const ScoreBadge = ({ score }) => {
  let color = "bg-red-100 text-red-700 border-red-200";
  let label = "Crítico";
  
  if (score >= 85) {
    color = "bg-emerald-100 text-emerald-700 border-emerald-200";
    label = "Excelente";
  } else if (score >= 70) {
    color = "bg-blue-100 text-blue-700 border-blue-200";
    label = "Bueno";
  } else if (score >= 50) {
    color = "bg-amber-100 text-amber-700 border-amber-200";
    label = "Revisar";
  }
  
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <Badge className={`${color} cursor-help`}>
            {score}/100
          </Badge>
        </TooltipTrigger>
        <TooltipContent>
          <div className="text-xs">
            <p className="font-semibold">{label}</p>
            <p className="text-slate-400 mt-1">
              Score basado en: Estado (30pts), Aprobación (30pts), Ad Strength (25pts), URL (15pts)
            </p>
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

const StatusCell = ({ value, type }) => {
  const getColor = () => {
    if (value.includes("🟢")) return "text-emerald-600";
    if (value.includes("🟡")) return "text-amber-600";
    if (value.includes("🟠")) return "text-orange-500";
    if (value.includes("🔴")) return "text-red-600";
    return "text-slate-500";
  };
  
  const tooltips = {
    status: {
      "🟢 Activo": "El anuncio está habilitado y corriendo",
      "🟡 Pausado": "El anuncio está pausado temporalmente",
      "🔴 Eliminado": "El anuncio fue eliminado",
      "⚪ Otro": "Estado desconocido"
    },
    approval: {
      "🟢 Aprobado": "Cumple con todas las políticas de Google Ads",
      "🟡 Aprobado Limitado": "Aprobado pero con restricciones en algunos países o públicos",
      "🔴 Rechazado": "Violación de políticas - requiere corrección",
      "🟠 En Revisión": "Pendiente de aprobación por Google",
      "⚪ Desconocido": "Estado de aprobación no disponible"
    },
    strength: {
      "🟢 Excelente": "Ad Strength óptimo - maximiza el rendimiento",
      "🟢 Bueno": "Buen nivel de optimización",
      "🟡 Promedio": "Hay oportunidades de mejora",
      "🔴 Pobre": "Necesita mejoras urgentes en assets y configuración",
      "⚪ N/A": "No aplica para este tipo de anuncio"
    },
    url: {
      "🟢 Con URL": "URL final configurada correctamente",
      "🔴 Sin URL": "Falta URL final - el anuncio no funcionará"
    }
  };
  
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <span className={`text-sm font-medium cursor-help ${getColor()}`}>
            {value}
          </span>
        </TooltipTrigger>
        <TooltipContent>
          <p className="text-xs">{tooltips[type]?.[value] || value}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

export const QAValidationView = ({ onBack }) => {
  const [clients, setClients] = React.useState([]);
  const [selectedClient, setSelectedClient] = React.useState("");
  const [validationResult, setValidationResult] = React.useState(null);
  const [isLoading, setIsLoading] = React.useState(false);
  const [isLoadingClients, setIsLoadingClients] = React.useState(true);
  
  // Alert configuration
  const [showAlertConfig, setShowAlertConfig] = React.useState(false);
  const [alertName, setAlertName] = React.useState("");
  const [scheduleInterval, setScheduleInterval] = React.useState("daily");
  const [alertOnReview, setAlertOnReview] = React.useState(true);
  const [notificationEmails, setNotificationEmails] = React.useState([]);
  const [emailInput, setEmailInput] = React.useState("");
  const [emailError, setEmailError] = React.useState("");
  const [isCreatingAlert, setIsCreatingAlert] = React.useState(false);
  
  // Existing alerts
  const [existingAlerts, setExistingAlerts] = React.useState([]);
  
  // Filters
  const [filterRating, setFilterRating] = React.useState("all");

  // Activity tracking (with duplicate prevention)
  const hasTrackedView = useRef(false);
  const trackActivity = useCallback(async (action, details = null) => {
    try {
      await axios.post(`${API}/track-activity`, {
        action,
        module: "qa_validation",
        details
      }, { withCredentials: true });
    } catch (e) { /* silent */ }
  }, []);

  // Track view on mount (only once)
  React.useEffect(() => {
    if (!hasTrackedView.current) {
      hasTrackedView.current = true;
      trackActivity("view");
    }
  }, [trackActivity]);

  React.useEffect(() => {
    fetchClients();
    fetchExistingAlerts();
  }, []);

  const fetchClients = async () => {
    try {
      setIsLoadingClients(true);
      const response = await axios.get(`${API}/clients`);
      setClients(response.data);
    } catch (error) {
      console.error("Error fetching clients:", error);
      toast.error("Error al cargar clientes");
    } finally {
      setIsLoadingClients(false);
    }
  };

  const fetchExistingAlerts = async () => {
    try {
      const response = await axios.get(`${API}/qa-alerts`);
      setExistingAlerts(response.data);
    } catch (error) {
      console.error("Error fetching QA alerts:", error);
    }
  };

  const validateAds = async () => {
    if (!selectedClient) return;
    
    try {
      setIsLoading(true);
      setValidationResult(null);
      const response = await axios.get(`${API}/qa/validate/${encodeURIComponent(selectedClient)}`);
      setValidationResult(response.data);
      
      // Track query with full details
      trackActivity("query", {
        client: selectedClient,
        total_ads_analyzed: response.data.total_ads_analyzed,
        excellent_count: response.data.excellent_count,
        good_count: response.data.good_count,
        review_count: response.data.review_count,
        critical_count: response.data.critical_count
      });
      
      if (response.data.ads.length === 0) {
        toast.info("Sin anuncios encontrados", {
          description: "No se encontraron anuncios modificados en los últimos 30 días"
        });
      } else if (response.data.critical_count > 0) {
        toast.warning(`${response.data.critical_count} anuncios críticos detectados`, {
          description: "Requieren atención inmediata"
        });
      } else {
        toast.success("Validación completada", {
          description: `${response.data.total_ads_analyzed} anuncios analizados`
        });
      }
    } catch (error) {
      console.error("Error validating:", error);
      toast.error("Error al validar", {
        description: error.response?.data?.detail || "No se pudo ejecutar la validación"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const validateEmail = (email) => {
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return emailRegex.test(email);
  };

  const handleAddEmail = () => {
    const email = emailInput.trim();
    if (!email) return;
    
    if (!validateEmail(email)) {
      setEmailError("Formato de email inválido");
      return;
    }
    
    if (notificationEmails.includes(email)) {
      setEmailError("Este email ya está agregado");
      return;
    }
    
    setNotificationEmails([...notificationEmails, email]);
    setEmailInput("");
    setEmailError("");
  };

  const handleRemoveEmail = (emailToRemove) => {
    setNotificationEmails(notificationEmails.filter(e => e !== emailToRemove));
  };

  const createAlert = async () => {
    if (!alertName.trim() || !selectedClient) {
      toast.error("Completa todos los campos requeridos");
      return;
    }

    try {
      setIsCreatingAlert(true);
      const response = await axios.post(`${API}/qa-alerts`, {
        alert_name: alertName.trim(),
        client_name: selectedClient,
        notification_emails: notificationEmails,
        schedule_interval: scheduleInterval,
        alert_on_review: alertOnReview
      });

      if (response.data.notification_status === "configured") {
        toast.success("Alerta QA creada", {
          description: "Las notificaciones por email están configuradas"
        });
      } else {
        toast.success("Alerta creada", {
          description: `La alerta "${alertName}" está activa`
        });
      }

      setAlertName("");
      setNotificationEmails([]);
      setShowAlertConfig(false);
      fetchExistingAlerts();
      
    } catch (error) {
      console.error("Error creating alert:", error);
      toast.error("Error al crear alerta");
    } finally {
      setIsCreatingAlert(false);
    }
  };

  const deleteAlert = async (alertId) => {
    try {
      await axios.delete(`${API}/qa-alerts/${alertId}`);
      toast.success("Alerta eliminada");
      fetchExistingAlerts();
    } catch (error) {
      toast.error("Error al eliminar alerta");
    }
  };

  const formatSchedule = (interval) => {
    const labels = {
      "1h": "Cada hora",
      "6h": "Cada 6 horas",
      "daily": "Diario",
      "weekly": "Semanal"
    };
    return labels[interval] || interval;
  };

  const filteredAds = React.useMemo(() => {
    if (!validationResult?.ads) return [];
    if (filterRating === "all") return validationResult.ads;
    
    const ratingMap = {
      "excellent": "🟢 Excelente",
      "good": "🟡 Bueno",
      "review": "🟠 Revisar",
      "critical": "🔴 Crítico"
    };
    
    return validationResult.ads.filter(ad => ad.rating === ratingMap[filterRating]);
  }, [validationResult, filterRating]);

  return (
    <div className="min-h-screen bg-[#e8eef3]">
      {/* Header */}
      <div className="border-b bg-white/80 backdrop-blur-sm sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="sm" onClick={onBack}>
                <ArrowLeft className="h-4 w-4 mr-2" />
                Volver
              </Button>
              <div className="h-6 w-px bg-slate-200" />
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-gradient-to-br from-teal-500 to-emerald-500">
                  <ClipboardCheck className="h-5 w-5 text-white" />
                </div>
                <div>
                  <h1 className="text-lg font-bold text-slate-800">QA de Anuncios</h1>
                  <p className="text-xs text-slate-500">Validación y control de calidad</p>
                </div>
              </div>
            </div>
            <Button variant="outline" size="sm" className="gap-2">
              <HelpCircle className="h-4 w-4" />
              Guía de QA
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8 space-y-8">
        {/* Intro Card */}
        <Card className="bg-gradient-to-r from-teal-50 to-emerald-50 border-teal-200">
          <CardContent className="py-4">
            <div className="flex items-start gap-3">
              <Info className="h-5 w-5 text-teal-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-slate-600">
                <p className="font-medium text-slate-700 mb-1">¿Qué valida este módulo?</p>
                <p>
                  Revisa los anuncios creados o modificados en los últimos 30 días, evaluando: 
                  <span className="font-medium"> estado de activación</span>, 
                  <span className="font-medium"> aprobación de políticas</span>, 
                  <span className="font-medium"> Ad Strength</span> y 
                  <span className="font-medium"> configuración de URLs</span>. 
                  Cada anuncio recibe un score de 0-100 para priorizar la revisión.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Client Selection & Validation */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Validar Anuncios</CardTitle>
            <CardDescription>
              Selecciona un cliente para ejecutar la validación de QA
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex gap-4">
              <div className="flex-1">
                <Select 
                  value={selectedClient} 
                  onValueChange={setSelectedClient}
                  disabled={isLoadingClients}
                >
                  <SelectTrigger className="h-11">
                    <SelectValue placeholder={isLoadingClients ? "Cargando clientes..." : "Seleccionar cliente"} />
                  </SelectTrigger>
                  <SelectContent>
                    {clients.map((client) => (
                      <SelectItem key={client.cliente} value={client.cliente}>
                        {client.cliente} ({client.account_count} cuentas)
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button 
                onClick={validateAds}
                disabled={!selectedClient || isLoading}
                className="bg-gradient-to-r from-teal-500 to-emerald-500 text-white"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Validando...
                  </>
                ) : (
                  <>
                    <Search className="h-4 w-4 mr-2" />
                    Validar
                  </>
                )}
              </Button>
              <Button 
                variant="outline"
                onClick={() => setShowAlertConfig(!showAlertConfig)}
                disabled={!selectedClient}
              >
                <Bell className="h-4 w-4 mr-2" />
                Configurar Alerta
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Alert Configuration Panel */}
        {showAlertConfig && selectedClient && (
          <Card className="border-teal-200 bg-teal-50/30">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Bell className="h-5 w-5 text-teal-500" />
                Configurar Alerta Programada de QA
              </CardTitle>
              <CardDescription>
                Recibe notificaciones automáticas cuando se detecten problemas en anuncios
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Nombre de la Alerta</Label>
                  <Input
                    placeholder="Ej: QA Anuncios Banco GyT"
                    value={alertName}
                    onChange={(e) => setAlertName(e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <Clock className="h-3 w-3" />
                    Frecuencia de Validación
                  </Label>
                  <Select value={scheduleInterval} onValueChange={setScheduleInterval}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="6h">Cada 6 horas</SelectItem>
                      <SelectItem value="daily">Diario</SelectItem>
                      <SelectItem value="weekly">Semanal</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <Mail className="h-3 w-3" />
                  Emails para Notificaciones
                </Label>
                <div className="flex gap-2">
                  <Input
                    type="email"
                    placeholder="correo@ejemplo.com"
                    value={emailInput}
                    onChange={(e) => { setEmailInput(e.target.value); setEmailError(""); }}
                    onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), handleAddEmail())}
                    className="flex-1"
                  />
                  <Button type="button" variant="outline" onClick={handleAddEmail}>
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
                {emailError && <p className="text-xs text-red-500">{emailError}</p>}
                {notificationEmails.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {notificationEmails.map((email) => (
                      <Badge key={email} variant="secondary" className="pr-1">
                        {email}
                        <button onClick={() => handleRemoveEmail(email)} className="ml-1 p-0.5 rounded-full hover:bg-slate-300">
                          <X className="h-3 w-3" />
                        </button>
                      </Badge>
                    ))}
                  </div>
                )}
              </div>

              <div className="flex items-center justify-between p-3 bg-white rounded-lg border">
                <div>
                  <Label className="text-sm font-medium">Incluir anuncios "Revisar"</Label>
                  <p className="text-xs text-slate-500">Notificar también cuando hay anuncios con score entre 50-70</p>
                </div>
                <Switch checked={alertOnReview} onCheckedChange={setAlertOnReview} />
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => setShowAlertConfig(false)}>
                  Cancelar
                </Button>
                <Button 
                  onClick={createAlert}
                  disabled={isCreatingAlert || !alertName.trim()}
                  className="bg-gradient-to-r from-teal-500 to-emerald-500 text-white"
                >
                  {isCreatingAlert ? (
                    <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Creando...</>
                  ) : (
                    "Crear Alerta"
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Validation Results */}
        {validationResult && (
          <>
            {/* Summary Cards */}
            <div className="grid md:grid-cols-5 gap-4">
              <SummaryCard
                title="Total Anuncios"
                value={validationResult.total_ads_analyzed}
                icon={ClipboardCheck}
                color="border-l-slate-400"
                tooltip="Anuncios modificados en los últimos 30 días"
              />
              <SummaryCard
                title="Excelentes"
                value={validationResult.excellent_count}
                icon={CheckCircle2}
                color="border-l-emerald-500"
                tooltip="Score ≥ 85: Sin problemas detectados"
              />
              <SummaryCard
                title="Buenos"
                value={validationResult.good_count}
                icon={CheckCircle2}
                color="border-l-blue-500"
                tooltip="Score 70-84: Configuración correcta"
              />
              <SummaryCard
                title="Revisar"
                value={validationResult.review_count}
                icon={AlertTriangle}
                color="border-l-amber-500"
                tooltip="Score 50-69: Oportunidades de mejora"
              />
              <SummaryCard
                title="Críticos"
                value={validationResult.critical_count}
                icon={XCircle}
                color="border-l-red-500"
                subtext={validationResult.critical_count > 0 ? "Acción requerida" : ""}
                tooltip="Score < 50: Problemas serios que afectan el rendimiento"
              />
            </div>

            {/* Ads Table */}
            {validationResult.ads.length > 0 && (
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="text-lg">Resultados de Validación</CardTitle>
                      <CardDescription>
                        {validationResult.ads.length} anuncios analizados - Ordenados por prioridad
                      </CardDescription>
                    </div>
                    <div className="flex items-center gap-2">
                      <Select value={filterRating} onValueChange={setFilterRating}>
                        <SelectTrigger className="w-40">
                          <SelectValue placeholder="Filtrar por" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">Todos</SelectItem>
                          <SelectItem value="critical">🔴 Críticos</SelectItem>
                          <SelectItem value="review">🟠 Revisar</SelectItem>
                          <SelectItem value="good">🟡 Buenos</SelectItem>
                          <SelectItem value="excellent">🟢 Excelentes</SelectItem>
                        </SelectContent>
                      </Select>
                      <Button variant="outline" size="sm" onClick={validateAds}>
                        <RefreshCw className="h-4 w-4 mr-2" />
                        Actualizar
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="text-xs font-medium uppercase">Score</TableHead>
                          <TableHead className="text-xs font-medium uppercase">Rating</TableHead>
                          <TableHead className="text-xs font-medium uppercase">Cuenta</TableHead>
                          <TableHead className="text-xs font-medium uppercase">Campaña</TableHead>
                          <TableHead className="text-xs font-medium uppercase">Anuncio</TableHead>
                          <TableHead className="text-xs font-medium uppercase">Estado</TableHead>
                          <TableHead className="text-xs font-medium uppercase">Aprobación</TableHead>
                          <TableHead className="text-xs font-medium uppercase">Strength</TableHead>
                          <TableHead className="text-xs font-medium uppercase">URL</TableHead>
                          <TableHead className="text-xs font-medium uppercase">Días</TableHead>
                          <TableHead className="text-xs font-medium uppercase">Problemas</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {filteredAds.map((ad, idx) => (
                          <TableRow key={idx} className={
                            ad.rating.includes("Crítico") ? "bg-red-50/50" :
                            ad.rating.includes("Revisar") ? "bg-amber-50/50" : ""
                          }>
                            <TableCell>
                              <ScoreBadge score={ad.quality_score} />
                            </TableCell>
                            <TableCell>
                              <Badge className={
                                ad.rating.includes("Excelente") ? "bg-emerald-100 text-emerald-700" :
                                ad.rating.includes("Bueno") ? "bg-blue-100 text-blue-700" :
                                ad.rating.includes("Revisar") ? "bg-amber-100 text-amber-700" :
                                "bg-red-100 text-red-700"
                              }>
                                {ad.rating}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-sm max-w-[100px] truncate" title={ad.account_name}>
                              {ad.account_name}
                            </TableCell>
                            <TableCell className="text-sm max-w-[120px] truncate" title={ad.campaign_name}>
                              {ad.campaign_name}
                            </TableCell>
                            <TableCell className="text-sm max-w-[150px] truncate" title={ad.ad_name}>
                              <div className="flex items-center gap-1">
                                {ad.ad_name}
                                <span className="text-xs text-slate-400">({ad.ad_type})</span>
                              </div>
                            </TableCell>
                            <TableCell>
                              <StatusCell value={ad.status_flag} type="status" />
                            </TableCell>
                            <TableCell>
                              <StatusCell value={ad.approval_flag} type="approval" />
                            </TableCell>
                            <TableCell>
                              <StatusCell value={ad.strength_flag} type="strength" />
                            </TableCell>
                            <TableCell>
                              <StatusCell value={ad.url_flag} type="url" />
                            </TableCell>
                            <TableCell className="text-sm text-slate-600">
                              {ad.days_since_update}d
                            </TableCell>
                            <TableCell className="max-w-[200px]">
                              <TooltipProvider>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <span className={`text-xs cursor-help ${
                                      ad.issues_detected === "Sin problemas" 
                                        ? "text-emerald-600" 
                                        : "text-red-600"
                                    }`}>
                                      {ad.issues_detected === "Sin problemas" 
                                        ? "✅ Sin problemas" 
                                        : `⚠️ ${ad.issues_detected.split(", ").length} problema(s)`}
                                    </span>
                                  </TooltipTrigger>
                                  <TooltipContent className="max-w-xs">
                                    <p className="text-xs">{ad.issues_detected}</p>
                                  </TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </CardContent>
              </Card>
            )}

            {validationResult.ads.length === 0 && (
              <Card className="border-emerald-200 bg-emerald-50/30">
                <CardContent className="py-12 text-center">
                  <CheckCircle2 className="h-12 w-12 text-emerald-500 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-emerald-700">Sin anuncios recientes</h3>
                  <p className="text-emerald-600 mt-2">
                    No se encontraron anuncios modificados en los últimos 30 días para {validationResult.client_name}
                  </p>
                </CardContent>
              </Card>
            )}
          </>
        )}

        {/* Existing Alerts Section */}
        {existingAlerts.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Alertas QA Programadas</CardTitle>
              <CardDescription>
                Validaciones automáticas configuradas
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Nombre</TableHead>
                    <TableHead>Cliente</TableHead>
                    <TableHead>Frecuencia</TableHead>
                    <TableHead>Notificaciones</TableHead>
                    <TableHead>Estado</TableHead>
                    <TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {existingAlerts.map((alert) => (
                    <TableRow key={alert.id}>
                      <TableCell className="font-medium">{alert.alert_name}</TableCell>
                      <TableCell>{alert.client_name}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1.5">
                          <Clock className="h-3.5 w-3.5 text-slate-400" />
                          {formatSchedule(alert.schedule_interval)}
                        </div>
                      </TableCell>
                      <TableCell>
                        {alert.notification_emails?.length > 0 ? (
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger>
                                <Badge className={
                                  alert.notification_status === "configured"
                                    ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                                    : "bg-blue-50 text-blue-700 border-blue-200"
                                }>
                                  {alert.notification_status === "configured" ? (
                                    <><CheckCircle2 className="h-3 w-3 mr-1" />Activo</>
                                  ) : (
                                    <><Mail className="h-3 w-3 mr-1" />{alert.notification_emails.length}</>
                                  )}
                                </Badge>
                              </TooltipTrigger>
                              <TooltipContent>
                                <div className="text-xs">
                                  {alert.notification_emails.map((e, i) => <div key={i}>{e}</div>)}
                                </div>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        ) : (
                          <span className="text-slate-400 text-xs">Sin configurar</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge className={
                          alert.status === "active"
                            ? "bg-emerald-50 text-emerald-700"
                            : "bg-slate-100 text-slate-600"
                        }>
                          {alert.status === "active" ? "Activa" : alert.status}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-red-500 hover:text-red-700 hover:bg-red-50"
                          onClick={() => deleteAlert(alert.id)}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};
